import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { alert } from 'actions/flash'
import { useUpdateMerchantMutation } from 'api/merchants'
import { isError } from 'api/utils'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import {
  markFormPersisted,
  setFormSubmitting,
} from 'slices/orderFeedbackCollection'
import {
  selectApiFormattedFormData,
  selectFormDisabled,
  selectIsFormDirty,
  selectIsFormSubmitting,
} from 'slices/orderFeedbackCollection/selectors'
import { useSettingsEditPermission } from './useSettingsEditPermission'

const t = buildTranslate('order_ratings.settings.feedback_collection')

export const useFeedbackCollectionSubmitForm = () => {
  const dispatch = useDispatch()
  const apiFormattedFormData = useSelector(selectApiFormattedFormData)
  const formDisabled = useSelector(selectFormDisabled)
  const isFormDirty = useSelector(selectIsFormDirty)
  const isFormSubmitting = useSelector(selectIsFormSubmitting)
  const [updateMerchant] = useUpdateMerchantMutation()

  // FR-60. Presentation only: the server already carries
  // `rbac_authorize!('manage', 'merchant_settings')` on PATCH /merchants/:id.
  // This keeps a call the viewer cannot make off the wire.
  const { editDisabled } = useSettingsEditPermission()

  const handleSubmit = useCallback(async () => {
    // Guard the handler too, not just the button. A disabled button is a UI
    // affordance; this is the one that holds if anything ever calls the handler
    // directly, and it keeps a no-op PATCH off the wire.
    if (editDisabled || formDisabled || isFormSubmitting || !isFormDirty) return

    dispatch(setFormSubmitting(true))
    try {
      const result = await updateMerchant({
        data: apiFormattedFormData,
      })

      if (isError(result)) {
        dispatch(
          alert({
            key: 'danger',
            message: t('alert.submit_error'),
            timeout: 5,
          })
        )
        return
      }

      // The edit is now the persisted value, so the pane goes clean and Publish
      // disables again until the next change.
      dispatch(markFormPersisted())
      dispatch(
        alert({
          key: 'success',
          message: t('alert.submit_success'),
          timeout: 5,
        })
      )
    } finally {
      // Always clear isFormSubmitting — covers the normal isError path AND
      // a thrown error from any middleware in the dispatch chain (interceptor
      // re-rejecting, future middleware wrapping). Without this the form stays
      // permanently disabled on a thrown rejection.
      dispatch(setFormSubmitting(false))
    }
  }, [
    apiFormattedFormData,
    updateMerchant,
    dispatch,
    editDisabled,
    formDisabled,
    isFormDirty,
    isFormSubmitting,
  ])

  return handleSubmit
}
